import {
  Card,
  Grid,
  RichTextReader,
  RichTextEditor,
  IconButton,
  Stack,
  Tooltip,
  GridBreak,
  DeleteIconButton,
  Box,
  DataDisplay,
  DELETE_WARNING,
  GrabIconButton,
} from "@compass/core";
import { ApplicationSection, SimpleDto } from "generated/openApi";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { useEffect, useState } from "react";
import { Input, Select } from "components";

type ApplicationSectionContainerProps = {
  pages: SimpleDto[];
  selected?: boolean;
  setSelected: (value: ApplicationSection | null) => void;
  section: ApplicationSection | undefined;
  updateSection: (value: ApplicationSection) => void;
  addPage: (pageNumber: number) => void;
  addSection: (displayOrder: number) => void;
  navigateEditMode: () => void;
};

export const ApplicationSectionContainer = ({
  pages,
  selected,
  setSelected,
  section,
  updateSection,
  addPage,
  addSection,
  navigateEditMode,
}: ApplicationSectionContainerProps) => {
  const [applicationSection, setApplicationSection] = useState<ApplicationSection | undefined>(section);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useEffect(() => {
    updateSection(applicationSection!);

  }, [applicationSection]);

  const deleteSection = (e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setApplicationSection(
        produce((draft) => {
          draft!.deleted = true;
        })
      );
    }
  };

  return (
    <>
      <Grid item xs={10}>
        <Card onClick={() => {
          setSelected(applicationSection!); 
          navigateEditMode();
        }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{ minWidth: "500px" }}
          >
          <Grid container spacing="sm">
            <Grid item xs={11}>
              <Grid container spacing="sm">
                <Grid item md={10} xs={12}>
                  {!selected && <DataDisplay label="Section">{applicationSection?.name}</DataDisplay>}
                  {selected && (
                    <Input
                      label="Name"
                      value={applicationSection?.name}
                      onChange={(e) =>
                        setApplicationSection(
                          produce((draft) => {
                            draft!.name = e.target.value;
                          })
                        )
                      }
                    />
                  )}
                </Grid>

                <GridBreak />

                <Grid item md={10} xs={12}>
                  {!selected && <RichTextReader label="Description" html={applicationSection?.description || ""} />}
                  {selected && (
                    <RichTextEditor
                      label="Description"
                      placeholder="Enter description..."
                      value={applicationSection?.description}
                      onChange={(value) =>
                        setApplicationSection(
                          produce((draft) => {
                            draft!.description = value!;
                          })
                        )
                      }
                    />
                  )}
                </Grid>

                <GridBreak />

                <Grid item xs={4}>
                  {!selected && (
                    <DataDisplay label="Page">{applicationSection?.pageIndex}</DataDisplay>
                  )}
                  {selected && (
                    <Select
                      label="Page"
                      value={pages.find(
                        (x) => x.id === applicationSection?.pageIndex
                      )}
                      options={pages}
                      getOptionLabel={(option) => option.name}
                      onChange={(e, value) =>
                        {
                          if (value.id == 0) {
                            // if they select "Add New Page", find highest page and increment by 1
                            var highestPage = Math.max(...pages.map(p => p.id));
                            addPage(highestPage + 1);
                            setApplicationSection(
                              produce((draft) => {
                                draft!.pageIndex = highestPage + 1
                              })
                            );
                          }
                          else {
                            setApplicationSection(
                              produce((draft) => {
                                draft!.pageIndex = value?.id;
                              })
                            );
                          }
                        }
                      }
                    />
                  )}
                </Grid>

                <Box />
              </Grid>
            </Grid>

            <Grid item xs={1} display="flex" direction="column" alignItems="center" justifyContent="space-between">
              <Box>
                {selected && (
                  <IconButton onClick={(e) => {e.stopPropagation(); setSelected(null)}}>
                    <ExpandLessIcon />
                  </IconButton>
                )}
              </Box>
              <Box>{isHovered && <GrabIconButton />}</Box>
              <Box />
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {selected && (
        <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
          <Card sx={{ maxWidth: "100px" }}>
            <Stack direction="column" spacing="md">
              <IconButton onClick={() => addSection(applicationSection?.displayOrder! + 1)}>
                <Tooltip title="Add New Question">
                  <AddCircleOutlineIcon />
                </Tooltip>
              </IconButton>

              <DeleteIconButton
                onClick={(e) => {
                  deleteSection(e);
                }}
              />
            </Stack>
          </Card>
        </Grid>
      )}
    </>
  );
};
